import { Component } from "@theme/component";

/**
 * Custom element that handles waitlist signup form submission to Klaviyo.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLInputElement} emailInput - The email input field.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} buttonText - The button text span.
 * @property {HTMLElement} buttonSpinner - The loading spinner element.
 * @property {HTMLElement} successMessage - The success message element.
 * @property {HTMLElement} errorMessage - The error message element.
 * @property {HTMLElement} errorText - The error text element.
 *
 * @extends Component<Refs>
 */
export class WaitlistSignupComponent extends Component {
  requiredRefs = [
    "form",
    "emailInput",
    "submitButton",
    "buttonText",
    "buttonSpinner",
    "successMessage",
    "errorMessage",
    "errorText",
  ];

  connectedCallback() {
    super.connectedCallback();
    // Client API uses company_id (public API key) for authentication
    // listId is stored but Client Subscription API handles channel subscriptions
    // List assignment may be handled via Klaviyo flows
    this.listId = this.dataset.listId;
    this.apiKey = this.dataset.apiKey;

    // Attach form submit handler
    this.refs.form.addEventListener("submit", this.handleSubmit);

    // Initialize visibility states - ensure everything starts hidden
    this.refs.successMessage.hidden = true;
    this.refs.errorMessage.hidden = true;
    this.refs.buttonSpinner.hidden = true;
    this.refs.form.hidden = false;
    this.setLoadingState(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.form.removeEventListener("submit", this.handleSubmit);
  }

  /**
   * Handles form submission to Klaviyo API.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const {
      form,
      emailInput,
      submitButton,
      buttonText,
      buttonSpinner,
      successMessage,
      errorMessage,
      errorText,
    } = this.refs;

    // Validate required fields
    if (!emailInput.value.trim()) {
      this.showError("Please enter your email address.");
      return;
    }

    // Validate email format
    if (!this.isValidEmail(emailInput.value)) {
      emailInput.setAttribute("aria-invalid", "true");
      this.showError("Please enter a valid email address.");
      return;
    }

    // Clear any previous errors
    this.hideError();
    this.hideSuccess();

    // Disable form and show loading state
    this.setLoadingState(true);

    try {
      // Prepare data for Klaviyo
      const profileData = {
        email: emailInput.value.trim(),
      };

      // Submit to Klaviyo
      await this.submitToKlaviyo(profileData);

      // Show success message
      this.showSuccess();
      form.reset();

      // Reset email input aria-invalid
      emailInput.setAttribute("aria-invalid", "false");
    } catch (error) {
      console.error("Waitlist signup error:", error);
      this.showError(
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      this.setLoadingState(false);
    }
  };

  /**
   * Submits profile data to Klaviyo using Client API subscription endpoint.
   * Reference: https://developers.klaviyo.com/en/reference/create_client_subscription
   *
   * @param {{email: string}} profileData - The profile data to submit.
   * @returns {Promise<void>}
   */
  async submitToKlaviyo(profileData) {
    if (!this.apiKey) {
      throw new Error(
        "Klaviyo configuration is missing. Please check your settings."
      );
    }

    // Build the payload according to Klaviyo Client Subscription API (JSON:API format)
    // The public API key (company_id) is passed as a query parameter
    /** @type {{email: string, subscriptions?: object}} */
    const profileAttributes = {
      email: profileData.email,
    };

    // Build subscriptions object for channel consent
    /** @type {{email: {marketing: {consent: string}}}} */
    const subscriptions = {
      email: {
        marketing: {
          consent: "SUBSCRIBED",
        },
      },
    };

    // Add subscriptions to profile attributes (they belong inside profile.data.attributes)
    profileAttributes.subscriptions = subscriptions;

    // Assemble the final payload
    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: profileAttributes,
            },
          },
        },
      },
    };

    // Add list relationship if listId is provided
    if (this.listId) {
      // @ts-ignore - relationships flow is dynamically added
      payload.data.relationships = {
        list: {
          data: {
            type: "list",
            id: this.listId,
          },
        },
      };
    }

    // Use Klaviyo Client API endpoint - designed for client-side use
    // Public API key (company_id) is passed as query parameter
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${this.apiKey}`,
      {
        method: "POST",
        headers: {
          accept: "application/vnd.api+json",
          "content-type": "application/vnd.api+json",
          revision: "2025-10-15",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      let errorMessage = `API error: ${response.status}`;
      try {
        const errorData = await response.json();
        // Klaviyo returns errors in JSON:API format
        if (errorData.errors && errorData.errors.length > 0) {
          errorMessage =
            errorData.errors[0].detail ||
            errorData.errors[0].title ||
            errorMessage;
        }
      } catch (e) {
        // If response isn't JSON, use status text or default message
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Shows the success message after successful subscription.
   */
  showSuccess() {
    const { successMessage, form, errorMessage } = this.refs;

    // Hide error message
    errorMessage.setAttribute("hidden", "");

    // Fade out the form
    /** @type {HTMLElement} */ (form).classList.add("fade-out");

    // Wait for fade-out transition to complete (300ms)
    setTimeout(() => {
      form.setAttribute("hidden", "");
      /** @type {HTMLElement} */ (form).classList.remove("fade-out");

      // Show and fade in the success message
      successMessage.removeAttribute("hidden");
      /** @type {HTMLElement} */ (successMessage).classList.add("fade-in");

      // Trigger reflow to ensure the element is visible before transition
      void (/** @type {HTMLElement} */ (successMessage).offsetWidth);

      // Remove fade-in class to trigger fade-in animation
      requestAnimationFrame(() => {
        /** @type {HTMLElement} */ (successMessage).classList.remove("fade-in");

        // Focus success message for accessibility after fade-in completes
        setTimeout(() => {
          successMessage.setAttribute("tabindex", "-1");
          successMessage.focus();
        }, 300);
      });
    }, 300);
  }

  /**
   * Hides the success message and shows the form.
   */
  hideSuccess() {
    const { successMessage, form } = this.refs;
    successMessage.hidden = true;
    successMessage.removeAttribute("tabindex");
    form.hidden = false;
  }

  /**
   * Shows the error message.
   *
   * @param {string} message - The error message to display.
   */
  showError(message) {
    const { errorMessage, errorText, form, successMessage } = this.refs;
    errorText.textContent = message;
    successMessage.hidden = true;
    errorMessage.hidden = false;
    errorMessage.setAttribute("tabindex", "-1");
    errorMessage.focus();
  }

  /**
   * Hides the error message.
   */
  hideError() {
    const { errorMessage } = this.refs;
    errorMessage.hidden = true;
    errorMessage.removeAttribute("tabindex");
  }

  /**
   * Sets the loading state of the form.
   *
   * @param {boolean} isLoading - Whether the form is in a loading state.
   */
  setLoadingState(isLoading) {
    const { submitButton, buttonText, buttonSpinner, emailInput } = this.refs;
    submitButton.disabled = isLoading;
    buttonText.hidden = isLoading;
    buttonSpinner.hidden = !isLoading;
    emailInput.disabled = isLoading;
  }

  /**
   * Validates an email address.
   *
   * @param {string} email - The email address to validate.
   * @returns {boolean} Whether the email is valid.
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

customElements.define("waitlist-signup-component", WaitlistSignupComponent);
